import React from "react";
import LegalPageLayout from "@/components/LegalPageLayout";
import { SITE } from "@/lib/site";

const sections = [
  {
    id: "overview",
    title: "Our security posture",
    body: (
      <>
        <p>
          Fixlr is built and operated for property and facilities teams who handle sensitive resident, supplier and compliance data. Security isn't a checkbox at the end of our roadmap — it's part of how we design, build, operate and review the platform every week.
        </p>
        <p>
          This page is a plain-English summary of what we do. If you need a deeper technical pack (security review, SOC 2 reports, pen-test summary, DPA), email <a href={`mailto:${SITE.email}`} className="text-mk-lime font-semibold">{SITE.email}</a>.
        </p>
      </>
    ),
  },
  {
    id: "infrastructure",
    title: "Infrastructure & hosting",
    body: (
      <>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Hosted in <strong>ISO 27001</strong>-certified cloud regions in the UK / EU.</li>
          <li>Production environments are isolated from development and staging.</li>
          <li>Network access is locked down via private VPCs, security groups and bastion hosts.</li>
          <li>Application secrets are stored in a managed secrets vault, never in source.</li>
          <li>Daily backups with 30-day rolling retention, encrypted at rest.</li>
        </ul>
      </>
    ),
  },
  {
    id: "encryption",
    title: "Encryption",
    body: (
      <>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>In transit</strong> — all traffic to and from Fixlr uses TLS 1.2+ with modern cipher suites.</li>
          <li><strong>At rest</strong> — application databases, file storage and backups are encrypted using AES-256.</li>
          <li><strong>Keys</strong> — managed via a dedicated KMS with rotation and audit logging.</li>
        </ul>
      </>
    ),
  },
  {
    id: "access",
    title: "Access control",
    body: (
      <>
        <ul className="list-disc pl-6 space-y-1.5">
          <li><strong>Customer access</strong> — role-based permissions, fine-grained per-property/site, with full audit logs of every read and write.</li>
          <li><strong>SSO & SCIM</strong> — SAML 2.0 / OIDC single sign-on and SCIM provisioning are included on Enterprise (Microsoft Entra, Okta, Google Workspace).</li>
          <li><strong>MFA</strong> — multi-factor authentication available on every plan; required by default on Enterprise.</li>
          <li><strong>Internal access</strong> — Fixlr engineers access production only through audited, time-boxed elevated sessions, on a strictly need-to-know basis. No engineer has standing access to customer data.</li>
        </ul>
      </>
    ),
  },
  {
    id: "data-handling",
    title: "Data handling",
    body: (
      <>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Customer data is logically separated per tenant, with row-level enforcement.</li>
          <li>We <strong>never use customer data to train external AI models</strong>. Chatbot prompts to our AI provider are used only to generate a reply for you.</li>
          <li>Data exports are available on demand from your account settings — CSV, JSON or PDF depending on the data type.</li>
          <li>On account closure, customer data is deleted from active systems within 30 days (backups follow the retention schedule).</li>
        </ul>
      </>
    ),
  },
  {
    id: "compliance",
    title: "Compliance & certifications",
    body: (
      <>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Built to meet UK GDPR and EU GDPR requirements as a data processor.</li>
          <li>Standard Data Processing Agreement (DPA) available — we'll countersign yours or provide ours.</li>
          <li>Our infrastructure providers are ISO 27001 and SOC 2 Type II certified; we maintain a vendor due-diligence register.</li>
          <li>SOC 2 Type II for Fixlr Ltd is in active progression — a current status letter is available on request.</li>
        </ul>
      </>
    ),
  },
  {
    id: "secure-sdlc",
    title: "Secure development",
    body: (
      <>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Code review and required-checks on every change before deploy.</li>
          <li>Static analysis (SAST), dependency scanning and secret scanning on every commit.</li>
          <li>Independent third-party penetration test at least annually; remediation tracked to closure with verification.</li>
          <li>Coordinated disclosure programme for researchers — see "Reporting" below.</li>
        </ul>
      </>
    ),
  },
  {
    id: "monitoring",
    title: "Monitoring & incident response",
    body: (
      <>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>24/7 alerting on availability, error rates, abnormal access and data volume.</li>
          <li>Documented incident response runbooks with severity tiers and RACI.</li>
          <li>Customer-impacting incidents are notified to admins within 24 hours of confirmation, with a post-incident review (PIR) inside 14 days.</li>
          <li>Status page and historical uptime available on request.</li>
        </ul>
      </>
    ),
  },
  {
    id: "business-continuity",
    title: "Business continuity",
    body: (
      <>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Multi-AZ database with automatic failover.</li>
          <li>RPO &lt; 1 hour, RTO &lt; 4 hours for the production application.</li>
          <li>DR drills run at least twice a year with documented outcomes.</li>
        </ul>
      </>
    ),
  },
  {
    id: "people",
    title: "People & training",
    body: (
      <>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Background checks for all staff with production access.</li>
          <li>Annual security and privacy training for all employees, with role-specific top-ups for engineering.</li>
          <li>Confidentiality obligations in every employment contract.</li>
        </ul>
      </>
    ),
  },
  {
    id: "reporting",
    title: "Reporting a security issue",
    body: (
      <>
        <p>
          If you've found a vulnerability, please email <a href={`mailto:${SITE.email}`} className="text-mk-lime font-semibold">{SITE.email}</a> with the subject line <em>"Security report"</em>. We acknowledge receipt within one business day.
        </p>
        <p>
          We don't take legal action against good-faith research conducted in line with our coordinated disclosure principles: don't access more data than needed to demonstrate the issue, don't degrade the service, and give us reasonable time to fix before public disclosure.
        </p>
      </>
    ),
  },
];

const Security = () => (
  <LegalPageLayout
    slug="security"
    eyebrow="Security"
    title="Security that's part of how we build, not an add-on."
    lastUpdated="15 January 2026"
    metaTitle="Security"
    intro="An overview of how Fixlr protects customer data — infrastructure, encryption, access, secure development, monitoring and incident response."
    sections={sections}
  />
);

export default Security;
